/**
 * Accessibility checker for page HTML content.
 * Returns an array of issue objects.
 */

const GENERIC_LINK_TEXT = new Set([
  'click here', 'here', 'read more', 'more', 'link', 'this',
  'learn more', 'click', 'go',
]);

/**
 * @param {string} html - The HTML content to check
 * @returns {Array<{id: string, severity: string, wcag: string, message: string, selector: string, fixable: boolean, fix?: object}>}
 */
export function checkAccessibility(html) {
  if (!html) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const issues = [];

  issues.push(...checkImageAltText(doc));
  issues.push(...checkHeadingHierarchy(doc));
  issues.push(...checkEmptyHeadings(doc));
  issues.push(...checkLinkQuality(doc));
  issues.push(...checkListStructure(doc));
  issues.push(...checkTableStructure(doc));
  issues.push(...checkMediaAccessibility(doc));
  issues.push(...checkColorContrast(doc));

  return issues;
}

function checkImageAltText(doc) {
  const issues = [];
  const images = doc.querySelectorAll('img');

  images.forEach((img, idx) => {
    const alt = img.getAttribute('alt');
    if (alt === null || alt === undefined) {
      issues.push({
        id: `img-alt-missing-${idx}`,
        severity: 'error',
        wcag: '1.1.1',
        message: `Image is missing alt text${img.src ? ` (${truncate(img.src, 40)})` : ''}.`,
        selector: `img:nth-of-type(${idx + 1})`,
        fixable: true,
        fix: { type: 'add-attribute', attribute: 'alt', value: '' },
      });
    } else if (alt.length > 120) {
      issues.push({
        id: `img-alt-long-${idx}`,
        severity: 'warning',
        wcag: '1.1.1',
        message: `Image alt text is too long (${alt.length} chars). Keep under 120 characters.`,
        selector: `img:nth-of-type(${idx + 1})`,
        fixable: false,
      });
    }
  });

  return issues;
}

function checkHeadingHierarchy(doc) {
  const issues = [];
  const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
  let prevLevel = 0;

  headings.forEach((heading, idx) => {
    const level = parseInt(heading.tagName[1], 10);
    if (prevLevel > 0 && level > prevLevel + 1) {
      issues.push({
        id: `heading-skip-${idx}`,
        severity: 'warning',
        wcag: '1.3.1',
        message: `Heading level skipped: <${heading.tagName.toLowerCase()}> follows <h${prevLevel}>. Don't skip heading levels.`,
        selector: `${heading.tagName.toLowerCase()}:nth-of-type(${idx + 1})`,
        fixable: false,
      });
    }
    prevLevel = level;
  });

  return issues;
}

function checkEmptyHeadings(doc) {
  const issues = [];
  const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');

  headings.forEach((heading, idx) => {
    const text = heading.textContent.trim();
    if (!text) {
      issues.push({
        id: `heading-empty-${idx}`,
        severity: 'error',
        wcag: '1.3.1',
        message: `Empty heading <${heading.tagName.toLowerCase()}>. Headings must contain text.`,
        selector: `${heading.tagName.toLowerCase()}:nth-of-type(${idx + 1})`,
        fixable: false,
      });
    }
  });

  return issues;
}

function checkLinkQuality(doc) {
  const issues = [];
  const links = doc.querySelectorAll('a');

  links.forEach((link, idx) => {
    const text = link.textContent.trim().toLowerCase();
    if (GENERIC_LINK_TEXT.has(text)) {
      issues.push({
        id: `link-generic-${idx}`,
        severity: 'warning',
        wcag: '2.4.4',
        message: `Link text "${link.textContent.trim()}" is not descriptive. Use text that describes the link destination.`,
        selector: `a:nth-of-type(${idx + 1})`,
        fixable: false,
      });
    }
  });

  return issues;
}

function checkListStructure(doc) {
  const issues = [];
  const listItems = doc.querySelectorAll('li');

  listItems.forEach((li, idx) => {
    const parent = li.parentElement;
    if (parent && parent.tagName !== 'UL' && parent.tagName !== 'OL') {
      issues.push({
        id: `list-orphan-${idx}`,
        severity: 'error',
        wcag: '1.3.1',
        message: 'List item <li> is not inside a <ul> or <ol>.',
        selector: `li:nth-of-type(${idx + 1})`,
        fixable: true,
        fix: { type: 'wrap', wrapper: 'ul' },
      });
    }
  });

  return issues;
}

function checkTableStructure(doc) {
  const issues = [];
  const tables = doc.querySelectorAll('table');

  tables.forEach((table, tIdx) => {
    // Check for caption or aria-label
    const caption = table.querySelector('caption');
    const ariaLabel = table.getAttribute('aria-label');
    if (!caption && !ariaLabel) {
      issues.push({
        id: `table-caption-${tIdx}`,
        severity: 'warning',
        wcag: '1.3.1',
        message: 'Table is missing a <caption> or aria-label for accessibility.',
        selector: `table:nth-of-type(${tIdx + 1})`,
        fixable: false,
      });
    }

    // Check th elements for scope
    const headers = table.querySelectorAll('th');
    headers.forEach((th, hIdx) => {
      if (!th.getAttribute('scope')) {
        issues.push({
          id: `table-th-scope-${tIdx}-${hIdx}`,
          severity: 'warning',
          wcag: '1.3.1',
          message: 'Table header <th> is missing scope attribute.',
          selector: `table:nth-of-type(${tIdx + 1}) th:nth-of-type(${hIdx + 1})`,
          fixable: true,
          fix: { type: 'add-attribute', attribute: 'scope', value: 'col' },
        });
      }
    });
  });

  return issues;
}

function checkMediaAccessibility(doc) {
  const issues = [];

  // Videos should have track elements
  const videos = doc.querySelectorAll('video');
  videos.forEach((video, idx) => {
    const tracks = video.querySelectorAll('track');
    if (tracks.length === 0) {
      issues.push({
        id: `video-captions-${idx}`,
        severity: 'warning',
        wcag: '1.2.1',
        message: 'Video is missing captions. Add a <track> element for accessibility.',
        selector: `video:nth-of-type(${idx + 1})`,
        fixable: false,
      });
    }
  });

  // Iframes should have title
  const iframes = doc.querySelectorAll('iframe');
  iframes.forEach((iframe, idx) => {
    if (!iframe.getAttribute('title')) {
      issues.push({
        id: `iframe-title-${idx}`,
        severity: 'error',
        wcag: '4.1.2',
        message: 'Iframe is missing a title attribute.',
        selector: `iframe:nth-of-type(${idx + 1})`,
        fixable: true,
        fix: { type: 'add-attribute', attribute: 'title', value: 'Embedded content' },
      });
    }
  });

  return issues;
}

function checkColorContrast(doc) {
  const issues = [];
  const elements = doc.querySelectorAll('[style]');

  elements.forEach((el, idx) => {
    const style = el.getAttribute('style') || '';
    const colorMatch = style.match(/(?:^|;)\s*color\s*:\s*([^;]+)/i);
    const bgMatch = style.match(/(?:^|;)\s*background(?:-color)?\s*:\s*([^;]+)/i);

    if (colorMatch && bgMatch) {
      const fgColor = parseColor(colorMatch[1].trim());
      const bgColor = parseColor(bgMatch[1].trim());

      if (fgColor && bgColor) {
        const ratio = getContrastRatio(fgColor, bgColor);
        if (ratio < 4.5) {
          issues.push({
            id: `contrast-${idx}`,
            severity: 'error',
            wcag: '1.4.3',
            message: `Low color contrast (${ratio.toFixed(1)}:1). WCAG requires at least 4.5:1 for normal text.`,
            selector: `[style]:nth-of-type(${idx + 1})`,
            fixable: false,
          });
        }
      }
    }
  });

  return issues;
}

// --- Utilities ---

function truncate(str, len) {
  if (str.length <= len) return str;
  return str.slice(0, len) + '...';
}

function parseColor(colorStr) {
  const match = colorStr.match(
    /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i
  );
  if (match) {
    return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])];
  }

  const hexMatch = colorStr.match(/^#([a-f0-9]{3,8})$/i);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    if (hex.length >= 6) {
      return [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16),
      ];
    }
  }

  return null;
}

function relativeLuminance(rgb) {
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function getContrastRatio(fg, bg) {
  const l1 = relativeLuminance(fg);
  const l2 = relativeLuminance(bg);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}
